import { DateTime } from 'luxon'

import type {
  Resource,
  ResourceLink,
  ResourceLinkList,
  ResourceMatchmakingSteamFieldsCsgoFragment,
  ResourceText,
  ResourceVideo,
  ResourceWorkshopSteamFieldsCsgoFragment,
  ResourceYoutube,
  ResourceYoutubeList
} from 'src/graphql/types'
import { MatchPlatform, ResourceFormatType } from 'src/graphql/types'

type PlatformResource = { platform: MatchPlatform }

export const isTextResource = (resource: Resource): resource is ResourceText =>
  resource.format === ResourceFormatType.Text

export const isVideoResource = (
  resource: Resource
): resource is ResourceVideo => resource.format === ResourceFormatType.Video

export const isYoutubeResource = (
  resource: Resource
): resource is ResourceYoutube => resource.format === ResourceFormatType.Youtube

export const isYoutubeListResource = (
  resource: Resource
): resource is ResourceYoutubeList =>
  resource.format === ResourceFormatType.YoutubeList

export const isLinkResource = (resource: Resource): resource is ResourceLink =>
  resource.format === ResourceFormatType.Link

export const isLinkListResource = (
  resource: Resource
): resource is ResourceLinkList =>
  resource.format === ResourceFormatType.LinkList

export const isMatchmakingSteamCsgoResource = (
  resource: Resource
): resource is ResourceMatchmakingSteamFieldsCsgoFragment => {
  if (resource.format !== ResourceFormatType.Matchmaking) return false
  return (
    (resource as unknown as PlatformResource).platform === MatchPlatform.Steam
  )
}

export const isWorkshopSteamCsgoResource = (
  resource: Resource
): resource is ResourceWorkshopSteamFieldsCsgoFragment => {
  if (resource.format !== ResourceFormatType.Workshop) return false
  return (
    (resource as unknown as PlatformResource).platform === MatchPlatform.Steam
  )
}

// A resource this build of the app can render. The API may serve a format a
// newer content build added (its fragment matches nothing, so the object
// arrives with no fields); such a resource is left out instead of crashing
// the match page.
export const isSupportedResource = (resource: Resource) =>
  [
    isTextResource,
    isVideoResource,
    isYoutubeResource,
    isYoutubeListResource,
    isLinkResource,
    isLinkListResource,
    isMatchmakingSteamCsgoResource,
    isWorkshopSteamCsgoResource
  ].some((isSupported) => isSupported(resource))

// The resources to show, count and flag as new: everything the caller reads
// from an assignment's resources goes through this, so the tab's count and
// its "new" marker agree with the list.
export const supportedResources = (resources: Resource[]) =>
  resources.filter(isSupportedResource)

const maxDaysOld = 7

// NOTE: Since this does not use useInterval, the value
// will only update on re-renders.
export const isNewResource = (resource: Resource) => {
  const createdAt = resource.createdAt
  const daysOld = DateTime.fromISO(createdAt).diffNow('days').days
  return daysOld <= 0 && Math.abs(daysOld) < maxDaysOld
}
